import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {
  onMounted, ref, withDefaults,
} from 'vue';

import ServicoOcorrencia from '@/servicos/ServicoOcorrencia';
import { IOcorrenciaParametrosConsulta } from '@/models/ParametrosRequisicao/Consulta/IOcorrenciaParametrosConsulta';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';

// Evita reclamação do TS sem types
declare const google: any;

interface Props {
  /** Opcional: se não passar, tenta ler do env */
  apiKey?: string;
  /** Map ID obrigatório p/ Advanced Marker; pode usar DEMO_MAP_ID em dev */
  mapId?: string;
  /** Quantidade de ocorrências mock */
  count?: number;
}
const LAT_DELTA = 0.02; // ~2.2 km
const LNG_DELTA = 0.025; // ~2.5 km

type Status = 'Aberta' | 'Em atendimento' | 'Concluída' | 'Cancelada';

type Ocorrencia = {
  id: string;
  numero: string;
  solicitante: string;
  status: Status;
  dataHora: string;
  lat: number;
  lng: number;
};

// util p/ carregar o script uma única vez

export default /*#__PURE__*/_defineComponent({
  __name: 'OcorrenciasMapa',
  props: {
    apiKey: {},
    mapId: {},
    count: { default: 10 }
  },
  setup(__props: any) {

const props = __props;


const servicoOcorrencia = new ServicoOcorrencia();
// Centro de Taió-SC
const CENTER = { lat: -27.116389, lng: -49.998056 };
// “Caixa” aproximada (~2.5 km) para sortear coordenadas dentro da cidade
function loadMaps(apiKey: string, mapId?: string): Promise<void> {
  if ((window as any).google?.maps) return Promise.resolve();

  // se já está carregando, apenas aguarda
  if (document.getElementById('gmap-script')) {
    return new Promise((resolve) => {
      // eslint-disable-next-line no-underscore-dangle
      (window as any)._gmapCbQueue.push(resolve);
    });
  }
  // eslint-disable-next-line no-underscore-dangle
  (window as any)._gmapCbQueue = [];

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = 'gmap-script';
    // v=beta para AdvancedMarker + arraste no futuro, libraries=marker e map_ids ajuda a evitar warnings
    const params = new URLSearchParams({
      key: apiKey,
      v: 'beta',
      libraries: 'marker',
      ...(mapId ? { map_ids: mapId } : {}),
    });
    s.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      resolve();
      // eslint-disable-next-line no-underscore-dangle
      (window as any)._gmapCbQueue.forEach((cb: () => void) => cb());
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function randBetween(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

function randCoord() {
  const lat = randBetween(CENTER.lat - LAT_DELTA, CENTER.lat + LAT_DELTA);
  const lng = randBetween(CENTER.lng - LNG_DELTA, CENTER.lng + LNG_DELTA);
  return { lat, lng };
}

function randNome() {
  const first = ['Ana', 'Beatriz', 'Carla', 'Diego', 'Eduardo', 'Felipe', 'Gabriela', 'Henrique', 'Isabela', 'João', 'Karina', 'Luís', 'Mariana', 'Nathalia', 'Otávio', 'Paulo', 'Rafaela', 'Sérgio', 'Thiago', 'Viviane'];
  const last = ['Silva', 'Santos', 'Souza', 'Oliveira', 'Pereira', 'Costa', 'Rodrigues', 'Almeida', 'Nascimento', 'Lima', 'Araújo', 'Fernandes', 'Gomes', 'Martins', 'Barbosa'];
  return `${first[Math.floor(Math.random() * first.length)]} ${last[Math.floor(Math.random() * last.length)]}`;
}

function randStatus(): Status {
  const all: Status[] = ['Aberta', 'Em atendimento', 'Concluída', 'Cancelada'];
  return all[Math.floor(Math.random() * all.length)];
}

function randNumero() {
  // OC-AAAAMMDD-XXXXX
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const y = now.getFullYear();
  const m = pad(now.getMonth() + 1);
  const d = pad(now.getDate());
  const seq = Math.floor(10000 + Math.random() * 90000);
  return `OC-${y}${m}${d}-${seq}`;
}

function randDataHora() {
  // dentro dos últimos 7 dias
  const now = Date.now();
  const past = now - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000);
  const d = new Date(past);
  const pad = (n: number) => String(n).padStart(2, '0');
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function makeMocks(count: number): Ocorrencia[] {
  return Array.from({ length: count }).map(() => {
    const { lat, lng } = randCoord();
    return {
      id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
      numero: randNumero(),
      solicitante: randNome(),
      status: randStatus(),
      dataHora: randDataHora(),
      lat,
      lng,
    };
  });
}

const mapRef = ref<HTMLElement | null>(null);

onMounted(async () => {
  const apiKey = 'AIzaSyDSH_kN9ZhgtXw3bg3LO-J9nUBC-4zD2TA';

  const mapId = props.mapId ?? (google?.maps?.Map?.DEMO_MAP_ID as string);

  if (!apiKey) {
    console.error('API key não fornecida. Passe via prop apiKey ou defina VITE_GMAPS_KEY/GOOGLE_MAPS_API_KEY.');
    return;
  }
  await loadMaps(apiKey, mapId);

  const map = new google.maps.Map(mapRef.value!, {
    center: CENTER,
    zoom: 13,
    mapId, // necessário para Advanced Markers
  });

  const info = new google.maps.InfoWindow();

  // const ocorrencias = makeMocks(props.count);

  const parametros:IOcorrenciaParametrosConsulta = {} as IOcorrenciaParametrosConsulta;
  parametros.apenasComCordenadas = true;
  parametros.ordenacao = [];
  parametros.ordenacao.push('DataOcorrenciaDESC');
  parametros.qtdeRegistrosPagina = 50;
  parametros.qtdeRegistrosTotal = 50;
  const consultaOcorrencias = await servicoOcorrencia.obterOcorrencias(parametros);
  // Usa Advanced Marker como “mapinha” simples
  // eslint-disable-next-line no-restricted-syntax
  for (const oc of consultaOcorrencias.dados) {
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: oc.latitude, lng: oc.longitude },
      title: oc.numero,
      // gmpDraggable: false (default) — só click
    });

    marker.addListener('click', () => {
      const latitude = oc.latitude || 0;
      const longitude = oc.longitude || 0;
      const html = `
        <div style="min-width:240px">
          <div style="font-weight:600; margin-bottom:4px;">Nº Ocorrência: ${oc.numero}</div>
          <div><b>Origem:</b> ${oc.nomeOrigem}</div>
          <div><b>Solicitante:</b> ${oc.solicitante}</div>
          <div><b>Responsável:</b> ${oc.agentePrincipal}</div>
          <div><b>Status:</b> ${servicoOcorrencia.obterNomeStatus(oc.status)}</div>
          <div><b>Data/Hora:</b> ${UtilitarioData.aplicaFormatoDataHoraMinuto(oc.data)}</div>
          <div style="margin-top:6px; font-size:12px; color:#555">
            Lat: ${latitude.toFixed(5)} • Lng: ${longitude.toFixed(5)}
          </div>
        </div>
      `;
      info.setContent(html);
      info.open({ map, anchor: marker });
    });
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "mapRef",
    ref: mapRef,
    style: {"height":"70vh","width":"100%"}
  }, null, 512))
}
}

})